// src/hooks/useEmployeeMaster.js

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  setDoc,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config.js";

// ────────────────────────────────────────────────────────────
// GENERIC SUBDOCUMENT HOOK
// ────────────────────────────────────────────────────────────

export function useEmployeeSubdocument(uid, subcollectionName) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uid) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, "users", uid, subcollectionName, "main"),
      (snap) => {
        setData(snap.exists() ? snap.data() : null);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(`[useEmployeeSubdocument] ${subcollectionName}:`, err);
        setError(err.message);
        setLoading(false);
      }
    );
    return unsub;
  }, [uid, subcollectionName]);

  const update = async (updates) => {
    if (!uid) return;
    const docRef = doc(db, "users", uid, subcollectionName, "main");
    await setDoc(docRef, { ...updates, updatedAt: serverTimestamp() }, { merge: true });
  };

  return { data, loading, error, update };
}

// ────────────────────────────────────────────────────────────
// GENERIC SUBCOLLECTION HOOK
// ────────────────────────────────────────────────────────────

export function useEmployeeSubcollection(uid, subcollectionName, orderField = "createdAt", orderDir = "desc") {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uid) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, "users", uid, subcollectionName),
      orderBy(orderField, orderDir)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(`[useEmployeeSubcollection] ${subcollectionName}:`, err);
        setError(err.message);
        setLoading(false);
      }
    );
    return unsub;
  }, [uid, subcollectionName, orderField, orderDir]);

  const add = async (item) => {
    if (!uid) return;
    const collRef = collection(db, "users", uid, subcollectionName);
    const docRef = await addDoc(collRef, {
      ...item,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  };

  const updateItem = async (itemId, updates) => {
    if (!uid) return;
    const docRef = doc(db, "users", uid, subcollectionName, itemId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  };

  const deleteItem = async (itemId) => {
    if (!uid) return;
    const docRef = doc(db, "users", uid, subcollectionName, itemId);
    await deleteDoc(docRef);
  };

  return { data, loading, error, add, update: updateItem, remove: deleteItem };
}

// ────────────────────────────────────────────────────────────
// SPECIFIC WRAPPER HOOKS
// ────────────────────────────────────────────────────────────

export function useEmployeePersonalDetails(uid) {
  return useEmployeeSubdocument(uid, "personal_details");
}

export function useEmployeeBankDetails(uid) {
  return useEmployeeSubdocument(uid, "bank_details");
}

export function useEmployeeInsurance(uid) {
  return useEmployeeSubdocument(uid, "insurance");
}

export function useEmployeeEducation(uid) {
  return useEmployeeSubcollection(uid, "education", "passingYear", "desc");
}

export function useEmployeeExperience(uid) {
  return useEmployeeSubcollection(uid, "experience", "startDate", "desc");
}

export function useEmployeeDocuments(uid) {
  return useEmployeeSubcollection(uid, "documents", "uploadedOn", "desc");
}

export function useEmployeeTimeline(uid) {
  return useEmployeeSubcollection(uid, "timeline", "timestamp", "desc");
}

// ────────────────────────────────────────────────────────────
// AUDIT LOG & TIMELINE FUNCTIONS
// ────────────────────────────────────────────────────────────

export async function logToTimeline(uid, eventType, title, description, changedBy) {
  const collRef = collection(db, "users", uid, "timeline");
  await addDoc(collRef, {
    eventType,
    title,
    description,
    changedBy,
    timestamp: serverTimestamp(),
  });
}

export async function logToAudit(employeeId, employeeName, actorId, actorName, actionType, targetField, beforeValue, afterValue) {
  const collRef = collection(db, "audit_logs");
  await addDoc(collRef, {
    userId: employeeId,
    userName: employeeName,
    actorId,
    actorName,
    actionType,
    targetField,
    beforeValue: String(beforeValue || ""),
    afterValue: String(afterValue || ""),
    timestamp: serverTimestamp(),
  });
}

// ────────────────────────────────────────────────────────────
// APPROVAL WORKFLOW HOOKS & FUNCTIONS
// ────────────────────────────────────────────────────────────

export function usePendingApprovals() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, "approval_requests"),
      where("status", "==", "PENDING"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[usePendingApprovals]", err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  return { data, loading };
}

export async function submitApprovalRequest({ employeeId, requestedBy, requestedByName, role, fields }) {
  const collRef = collection(db, "approval_requests");
  const docRef = await addDoc(collRef, {
    employeeId,
    requestedBy,
    requestedByName,
    role,
    fields,
    status: "PENDING",
    comments: "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function approveRequest(requestId, reviewerId, reviewerName) {
  const reqRef = doc(db, "approval_requests", requestId);
  const reqSnap = await getDoc(reqRef);
  if (!reqSnap.exists()) throw new Error("Request not found");

  const reqData = reqSnap.data();
  const { employeeId, fields, requestedByName } = reqData;

  const userFields = {};
  const personalFields = {};
  const bankFields = {};

  const userSchemaKeys = [
    "displayName", "firstName", "middleName", "lastName", "role",
    "departmentId", "designationId", "gradeId", "shiftId", "reportingManagerId",
    "hrManagerId", "companyId", "branchId", "costCenterId", "status",
    "joiningDate", "confirmationDate", "probationPeriod", "employmentType",
    "employeeCategoryId", "isActive"
  ];
  
  const personalSchemaKeys = [
    "fatherName", "motherName", "spouseName", "children",
    "aadhaar", "pan", "passport", "drivingLicense", "voterId",
    "passportExpiry", "visa", "permanentAddress", "currentAddress"
  ];

  const bankSchemaKeys = [
    "bankName", "branch", "accountNumber", "ifscCode", "accountHolder", "upiId",
    "isSalaryAccount", "ctc", "basicSalary", "hra", "allowances", "taxRegime",
    "pfApplicable", "pfNumber", "esiApplicable", "esiNumber", "professionalTax",
    "lwf", "tdsCategoryId"
  ];

  for (const [key, val] of Object.entries(fields)) {
    if (userSchemaKeys.includes(key)) {
      userFields[key] = val;
    } else if (personalSchemaKeys.includes(key)) {
      personalFields[key] = val;
    } else if (bankSchemaKeys.includes(key)) {
      bankFields[key] = val;
    }
  }

  const promises = [];

  if (Object.keys(userFields).length > 0) {
    promises.push(updateDoc(doc(db, "users", employeeId), {
      ...userFields,
      updatedAt: serverTimestamp(),
    }));
  }

  if (Object.keys(personalFields).length > 0) {
    promises.push(setDoc(doc(db, "users", employeeId, "personal_details", "main"), {
      ...personalFields,
      updatedAt: serverTimestamp(),
    }, { merge: true }));
  }

  if (Object.keys(bankFields).length > 0) {
    promises.push(setDoc(doc(db, "users", employeeId, "bank_details", "main"), {
      ...bankFields,
      updatedAt: serverTimestamp(),
    }, { merge: true }));
  }

  // Update request status
  promises.push(updateDoc(reqRef, {
    status: "APPROVED",
    reviewedBy: reviewerId,
    reviewedByName: reviewerName,
    updatedAt: serverTimestamp(),
  }));

  // Log to timeline
  promises.push(logToTimeline(
    employeeId,
    "FieldUpdate",
    "Profile Changes Approved",
    `Requested updates by ${requestedByName} were approved by ${reviewerName}. Changed fields: ${Object.keys(fields).join(", ")}.`,
    reviewerName
  ));

  // Log to audit log
  promises.push(logToAudit(
    employeeId,
    requestedByName,
    reviewerId,
    reviewerName,
    "WRITE",
    "approval_requests",
    JSON.stringify(fields),
    "APPROVED"
  ));

  await Promise.all(promises);
}

export async function rejectRequest(requestId, reviewerId, reviewerName, comments) {
  const reqRef = doc(db, "approval_requests", requestId);
  const reqSnap = await getDoc(reqRef);
  if (!reqSnap.exists()) throw new Error("Request not found");

  const reqData = reqSnap.data();
  const { employeeId, requestedByName, fields } = reqData;

  await updateDoc(reqRef, {
    status: "REJECTED",
    reviewedBy: reviewerId,
    reviewedByName: reviewerName,
    comments: comments || "Changes rejected by HR Manager",
    updatedAt: serverTimestamp(),
  });

  await logToTimeline(
    employeeId,
    "FieldUpdate",
    "Profile Changes Rejected",
    `Requested updates by ${requestedByName} were rejected by ${reviewerName}. Comments: ${comments || "None"}.`,
    reviewerName
  );

  await logToAudit(
    employeeId,
    requestedByName,
    reviewerId,
    reviewerName,
    "WRITE",
    "approval_requests",
    JSON.stringify(fields),
    "REJECTED"
  );
}

// ────────────────────────────────────────────────────────────
// MASTER DATA HOOKS & SEEDING
// ────────────────────────────────────────────────────────────

export function useMasterCollection(collectionName) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, collectionName), where("active", "==", true));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(`[useMasterCollection] ${collectionName}:`, err);
        setLoading(false);
      }
    );
    return unsub;
  }, [collectionName]);

  return { data, loading };
}

export async function seedEmployeeMasterData() {
  const seedCollection = async (collectionName, items) => {
    const collRef = collection(db, collectionName);
    const snap = await getDocs(query(collRef, limit(1)));
    if (snap.empty) {
      for (const item of items) {
        await addDoc(collRef, {
          ...item,
          active: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      console.log(`Seeded ${collectionName}`);
    }
  };

  await seedCollection("companies", [
    { code: "COMP-001", name: "TechCorp Solutions Pvt Ltd" },
    { code: "COMP-002", name: "Samriddhi Anveshana" }
  ]);

  await seedCollection("branches", [
    { code: "BR-HYD", name: "Hyderabad - HITEC City", address: "HITEC City, Hyderabad, India" },
    { code: "BR-BLR", name: "Bangalore - Whitefield", address: "Whitefield, Bangalore, India" }
  ]);

  await seedCollection("grades", [
    { code: "G1", name: "Grade 1 (Intern)", level: 1 },
    { code: "G2", name: "Grade 2 (Associate)", level: 2 },
    { code: "G3", name: "Grade 3 (Engineer)", level: 3 },
    { code: "G4", name: "Grade 4 (Senior)", level: 4 },
    { code: "G5", name: "Grade 5 (Lead/Manager)", level: 5 }
  ]);

  await seedCollection("cost_centers", [
    { code: "CC-101", name: "CC-PLATFORM", budgetOwner: "Engineering Lead" },
    { code: "CC-102", name: "CC-MARKETING", budgetOwner: "Marketing Head" },
    { code: "CC-103", name: "CC-HR", budgetOwner: "HR Director" },
    { code: "CC-104", name: "CC-FINANCE", budgetOwner: "CFO" }
  ]);

  await seedCollection("employee_categories", [
    { code: "CAT-TECH", name: "Technical Staff" },
    { code: "CAT-MGMT", name: "Management Staff" },
    { code: "CAT-SUPP", name: "Support Staff" }
  ]);

  await seedCollection("designations", [
    { code: "DES-001", name: "Software Engineer" },
    { code: "DES-002", name: "Senior Software Engineer" },
    { code: "DES-003", name: "Technical Lead" },
    { code: "DES-004", name: "QA Analyst" },
    { code: "DES-005", name: "HR Executive" },
    { code: "DES-006", name: "Financial Analyst" }
  ]);

  await seedCollection("skills", [
    { code: "SK-JS", name: "JavaScript", category: "Technical" },
    { code: "SK-REACT", name: "React", category: "Technical" },
    { code: "SK-NODE", name: "Node.js", category: "Technical" },
    { code: "SK-AWS", name: "AWS", category: "Cloud" },
    { code: "SK-FIREBASE", name: "Firebase", category: "Cloud" }
  ]);

  await seedCollection("certifications", [
    { code: "CERT-AWS-SA", name: "AWS Certified Solutions Architect", issuer: "Amazon Web Services" },
    { code: "CERT-CSM", name: "Certified ScrumMaster", issuer: "Scrum Alliance" }
  ]);
}
